import { apiBlobWithHeaders, apiRequest } from './httpClient';

// Same apiRequest as every other service (pricingApi.js, meetingApi.js,
// adminApi.js) - same base URL, same credentials/cookie behavior, same
// 401 refresh-and-retry. Nothing here has its own HTTP configuration.

// The provider's hosted checkout script. Loaded lazily rather than in
// index.html: it is a third-party script needed on exactly one page, and
// putting it in the document head would make every route - including the
// signed-out login page - wait on a request to the provider.
//
// Deliberately not an npm dependency either. The provider serves the
// checkout from its own origin so that fixes and compliance updates
// apply without a redeploy; a bundled copy would defeat that, and the
// frontend currently has no runtime dependencies beyond React.
const CHECKOUT_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

let checkoutScriptPromise = null;

export function loadCheckoutScript() {
  // Cached promise, so several rapid clicks share ONE script insertion
  // rather than each appending another tag - the same shared-in-flight
  // pattern httpClient.js uses for its token refresh.
  if (checkoutScriptPromise) return checkoutScriptPromise;

  checkoutScriptPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      reject(new Error('Checkout is only available in a browser.'));
      return;
    }

    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }

    const existing = document.querySelector(`script[src="${CHECKOUT_SCRIPT_SRC}"]`);

    if (existing) {
      existing.addEventListener('load', () => resolve(window.Razorpay));
      existing.addEventListener('error', () =>
        reject(new Error('Could not load the payment checkout.'))
      );
      return;
    }

    const script = document.createElement('script');
    script.src = CHECKOUT_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => {
      // Clear the cached promise so a later attempt can retry - a
      // transient network failure must not permanently disable checkout
      // for the rest of the session.
      checkoutScriptPromise = null;
      reject(new Error('Could not load the payment checkout.'));
    };

    document.body.appendChild(script);
  });

  return checkoutScriptPromise;
}

// Opens a provider subscription for the signed-in user.
//
// Sends a plan id and a cycle and NOTHING ELSE - there is deliberately
// no amount parameter here or anywhere in the API. The price, currency
// and provider plan id are all resolved server-side from the plan row
// (backend/services/billing/billing_service.py's start_checkout), so a
// tampered client cannot name its own price.
//
// Returns { provider_subscription_id, key_id, plan_name, amount,
// currency, short_url }. `amount` is for DISPLAY only.
export function startCheckout(planId, billingCycle) {
  return apiRequest('/api/billing/checkout', {
    method: 'POST',
    body: { plan_id: planId, billing_cycle: billingCycle },
  });
}

// Reports the browser checkout handler's result back to the backend.
//
// GRANTS NOTHING. The backend verifies the signature for tamper
// detection and answers { status: 'processing' | 'active' }; entitlement
// arrives only from the provider's webhook. A 'processing' answer is the
// normal case - poll getMySubscription() until the plan appears.
//
// Field names match the provider's own handler response, so the object
// it hands us is forwarded without renaming - one less place for a typo
// to become a silent verification failure.
export function verifyCheckout(handlerResponse) {
  return apiRequest('/api/billing/verify', {
    method: 'POST',
    body: {
      razorpay_payment_id: handlerResponse.razorpay_payment_id,
      razorpay_subscription_id: handlerResponse.razorpay_subscription_id,
      razorpay_signature: handlerResponse.razorpay_signature,
    },
  });
}

// The signed-in user's own billing state - what they are paying for and
// when they will next be charged. Returns { billing_enabled,
// payment_subscription }.
//
// billing_enabled false means this deployment has no payment provider
// configured, which is a supported state: provider-dependent purchase
// and cancellation actions are hidden, while local payment history stays
// available. Same pattern as UsagePanel reading `enforced` from
// getMySubscription().
export function getMyBilling() {
  return apiRequest('/api/billing/me');
}

// PUBLIC: the pricing page as the backend describes it - five cards in
// display order (Free, Meeting Pass, the paid plans, Custom / Team), the
// shared feature list, the FAQ and the policy behind them. Every price,
// allowance, badge and button label comes from here.
export function getPricingCatalogue() {
  return apiRequest('/api/billing/plans');
}

// The signed-in user's plan, meetings left, renewal/expiry date and the
// next action to offer - one object, decided server-side
// (backend/services/billing/entitlement_service.py).
export function getEntitlement() {
  return apiRequest('/api/billing/entitlement');
}

// Stops the subscription renewing, at the END of the period already
// paid for. The backend never cancels immediately - the subscriber keeps
// their plan until access_until, which is what the UI must say. An
// immediate cancellation is a refund decision and belongs to an admin.
//
// Returns { status: 'cancelling', access_until }.
export function cancelSubscription() {
  return apiRequest('/api/billing/cancel', { method: 'POST' });
}

// The signed-in user's own charge history, newest first.
//
// Served from the backend's own payments table rather than fetched from
// the provider, so it still answers "what have I been charged" when
// Razorpay is unreachable. Razorpay has no hosted customer portal to
// redirect to, which is why this page has to exist at all.
// Returns { items, total, limit, offset } - the backend paginates this
// (backend/routers/billing.py) since the table grows by one row per
// billing cycle for as long as a subscriber stays paid.
export function getPaymentHistory({ limit = 50, offset = 0 } = {}) {
  return apiRequest(`/api/billing/payments?limit=${limit}&offset=${offset}`);
}

// Authenticated PDF fetch through the same cookie + refresh flow as every
// other API call. The caller owns the browser download gesture/object URL;
// this layer returns the server filename alongside the Blob.
export function downloadPaymentReceipt(paymentId) {
  return apiBlobWithHeaders(`/api/billing/payments/${paymentId}/receipt.pdf`);
}

// Machine-readable codes the backend attaches to a 409 for billing
// specifically (backend/services/billing/billing_service.py's
// BillingActionError). Branch on THESE, never on the human message,
// which is copy and may be reworded - same contract as pricingApi.js's
// USAGE_ERROR_CODES.
export const BILLING_ERROR_CODES = {
  // The plan exists and is active but was never synced to the provider
  // (created during a provider outage, or predating billing). An admin
  // pressing Sync fixes it - so this is "try again shortly", not
  // "this plan is wrong".
  PLAN_NOT_SYNCED: 'PLAN_NOT_SYNCED',
  // Moving TO the free plan is a downgrade, not a purchase - it goes
  // through the plan-change request flow instead.
  PLAN_IS_FREE: 'PLAN_IS_FREE',
  ALREADY_SUBSCRIBED: 'ALREADY_SUBSCRIBED',
  SUBSCRIPTION_NOT_FOUND: 'SUBSCRIPTION_NOT_FOUND',
  // Cancelling something that was never paid for - a free-plan user, or
  // one whose subscription already lapsed.
  NO_PAID_SUBSCRIPTION: 'NO_PAID_SUBSCRIPTION',
  CHECKOUT_SIGNATURE_INVALID: 'CHECKOUT_SIGNATURE_INVALID',
  // 503, not 409: no provider is configured for this deployment.
  BILLING_NOT_CONFIGURED: 'BILLING_NOT_CONFIGURED',
};

export function isBillingError(error) {
  return Boolean(error) && Object.values(BILLING_ERROR_CODES).includes(error.code);
}

// How long to wait for the webhook after a successful checkout before
// telling the user it is still processing.
//
// The webhook usually lands within a second, but it travels a separate
// network path from the browser callback, so the UI polls rather than
// assuming. Never grants anything client-side - it only re-reads what
// the backend already wrote.
export const ACTIVATION_POLL_ATTEMPTS = 6;
export const ACTIVATION_POLL_INTERVAL_MS = 1000;
